import {Injectable} from '@angular/core';
import {Params} from '@angular/router';
import {SearchTimeslot} from 'src/app/types/SearchTimeslot';
import {SearchState} from '../../types/SearchState';
import {ToolsService} from '../tools.service';
import {DefaultValuesService} from '../default-values.service';
import {EnumsService} from '../enums.service';
import {SearchFilterCustomerFeatureTypeValueEnum} from '../../enums/SearchEnums';
import {UrlTermsSymbolsEnum, UrlTermsSymbolTimeslotsEnum} from '../../enums/SeoEnums';
import {RoutesAddonsEnums, RoutesQueryParamsEnum} from '../../enums/RoutesEnums';
import {SearchHelperService} from '../search-helper.service';

/* NOT USED NOW */
// FIXME: Remove this service after cleaning up the code

@Injectable({
  providedIn: 'root',
})
export class SearchRoutingParamsResolveService {
  private readonly urlPathProcessorsMap: Array<Function>;
  private pageWasSet: boolean = false;
  private state: SearchState;

  constructor(
    private tools: ToolsService,
    private enumsService: EnumsService,
    private defaults: DefaultValuesService,
    private searchHelper: SearchHelperService,
  ) {
    if (this.tools.isNil(this.urlPathProcessorsMap)) {
      this.urlPathProcessorsMap = [];
      this.createUrlPathProcessorsMap();
    }
  }

  public getResolvedState(routeParams: Params, queryParams: Params, urlPath?: string): SearchState {
    this.pageWasSet = false;
    this.state = this.tools.clone(this.defaults.getDefaultSearchState());
    if (!this.tools.isNil(urlPath)) {
      this.state.urlPath = urlPath;
    }
    this.resolveQueryParams(queryParams);
    this.resolveRouteParams(routeParams);
    this.setDefaultSearchTab();
    if (this.state.isValid) {
      this.state.isValid = this.isStateValuesValid(this.state);
    }
    return this.tools.clone(this.state);
  }

  private isStateValuesValid(state: SearchState): boolean {
    let isValid: boolean = true;
    if (
      state.caregiverUnitTypeUrlPath === '' &&
      state.careServiceTypeUrlPath === '' &&
      state.symptomUrlPath === '' &&
      state.placeUrlPath === '' &&
      state.freeSearchTerm === ''
    ) {
      isValid = false;
    }
    return isValid;
  }

  private updateCareproviderUnitType(urlPath: string): void {
    this.state.caregiverUnitTypeUrlPath = urlPath;
  }

  private updateServiceType(urlPath: string): void {
    this.state.careServiceTypeUrlPath = urlPath;
  }

  private updatePlace(urlPath: string): void {
    this.state.placeUrlPath = urlPath;
    if (urlPath !== '') {
      this.state.lat = 0;
      this.state.lon = 0;
      this.state.locationSearch = false;
    }
  }

  private updateLat(lat: number): void {
    if (this.isCoordValid(lat)) {
      this.state.lat = lat;
      this.state.locationSearch = !this.tools.isNil(lat);
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
      this.state.lat = 0;
      this.state.locationSearch = false;
    }
  }

  private updateLon(lon: number): void {
    if (this.isCoordValid(lon)) {
      this.state.lon = lon;
      this.state.locationSearch = !this.tools.isNil(lon);
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
    }
  }

  private updateDistance(distance: number): void {
    if (!this.tools.isNil(distance)) {
      this.state.distance = distance;
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
      this.state.distance = null;
    }
  }

  private updateFreeSearchTerm(term: string): void {
    this.state.freeSearchTerm = term;
  }

  private updateOpenNow(on: string): void {
    if (this.isBoolFilterValueValid(on)) {
      this.state.openNow = parseInt(on, 10) === 1 ? true : false;
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
    }
  }

  private updateOpenWeekend(ow: string): void {
    if (this.isBoolFilterValueValid(ow)) {
      this.state.openWeekend = parseInt(ow, 10) === 1 ? true : false;
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
    }
  }

  private updateCustomerFeatureType(cf: string): void {
    const enumKey: string = this.enumsService.getEnumKeyByEnumValue(SearchFilterCustomerFeatureTypeValueEnum, cf);
    if (!this.tools.isNil(enumKey)) {
      this.state.customerFeatureType = SearchFilterCustomerFeatureTypeValueEnum[enumKey];
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
    }
  }

  private isCoordValid(value: number): boolean {
    return parseFloat(value.toString()).toString() === value.toString();
  }

  private isBoolFilterValueValid(value: string): boolean {
    return value === '0' || value === '1';
  }

  private createUrlPathProcessorsMap() {
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE] = (urlPath: string) =>
      this.updateCareproviderUnitType(urlPath);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.SERVICE_TYPE] = (urlPath: string) => this.updateServiceType(urlPath);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.SYMPTOM] = (urlPath: string) => this.updateSymptom(urlPath);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.PLACE] = (urlPath: string) => this.updatePlace(urlPath);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.FREE] = (term: string) => this.updateFreeSearchTerm(term);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.LATITUDE] = (lat: number) => this.updateLat(lat);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.LONGITUDE] = (lon: number) => this.updateLon(lon);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.DISTANCE] = (distance: number) => this.updateDistance(distance);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.OPEN_NOW] = (on: string) => this.updateOpenNow(on);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.OPEN_WEEKEND] = (ow: string) => this.updateOpenWeekend(ow);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.CUSTOMER_FEATURE_TYPE] = (cf: string) =>
      this.updateCustomerFeatureType(cf);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.PAGE] = (page: number) => this.updatePage(page);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.TIMESLOT] = (ts: string[]) => this.updateTimeslot(ts);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.ATTRIBUTE] = (att: string[]) => this.updateAttributes(att);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.DEBUG_FLAGS] = (flags: string) => this.updateDebugFlags(flags);
    // URL query params to just ignore (marketing, campaigns and stuff)
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.AFTER_REDIRECT] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.GOOGLE_AD_PREVIEW] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.GOOGLE_AD_PREVIEW_IU] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.GOOGLE_AD_PREVIEW_GDFP] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.GOOGLE_AD_PREVIEW_LINEID] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.GOOGLE_AD_PREVIEW_CREATIVEID] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.GCLID] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.UTM_CAMPAIGN] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.UTM_SOURCE] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.UTM_MEDIUM] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.UTM_TERM] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.UTM_CONTENT] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.UTM_SIZE] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_STEP] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.FBCLID] = () => {};
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.FBQBTID] = () => {};
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.FORCE_CONTENT_PAGE] = (f: number) => this.updateForceContentPage(f);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.FORCE_SEARCH_RESULTS] = (f: number) =>
      this.updateForceSearchResults(f);
    this.urlPathProcessorsMap[UrlTermsSymbolsEnum.TAB] = (tab: string) => this.updateTab(tab);
  }

  private resolveRouteParams(params: Params): void {
    const paramsKeys: string[] = this.getParamsKeys([
      params.urlPath1,
      params.urlPath2,
      params.urlPath3,
      params.urlPath4,
    ]);
    const possibleTypes: string[] = [
      UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE,
      UrlTermsSymbolsEnum.SERVICE_TYPE,
      UrlTermsSymbolsEnum.SYMPTOM,
      UrlTermsSymbolsEnum.PLACE,
    ];

    const arePossibleTypesInRoute = paramsKeys.filter((value) => possibleTypes.indexOf(value) >= 0);

    if (arePossibleTypesInRoute.length > 0) {
      const usedTypes: string[] = [];
      this.processUrlPatch(params.urlPath1, usedTypes);
      this.processUrlPatch(params.urlPath2, usedTypes);
      this.processUrlPatch(params.urlPath3, usedTypes);
      this.processUrlPatch(params.urlPath4, usedTypes);
      this.resetUnusedUrlPathTypes(possibleTypes, usedTypes);
    }
  }

  private getParamsKeys(params: string[]): string[] {
    const keys: string[] = [];

    params.forEach((value) => {
      if (!this.tools.isNil(value)) {
        const key = this.getUrlPathType(value);

        if (key !== '') {
          keys.push(key);
        }
      }
    });

    return keys;
  }

  private processUrlPatch(urlPath: string, usedTypes: string[]): void {
    if (!this.tools.isNil(urlPath) && !this.tools.isNil(this.getUrlPathType(urlPath))) {
      if (
        this.getUrlPathType(urlPath) !== '' &&
        !this.tools.isNil(this.urlPathProcessorsMap[this.getUrlPathType(urlPath)])
      ) {
        this.urlPathProcessorsMap[this.getUrlPathType(urlPath)](this.getUrlPath(urlPath));
        usedTypes.push(this.getUrlPathType(urlPath));
      } else {
        this.state.isRedirectToStateBasedUrlRequired = true;
      }
    }
  }

  private resetUnusedUrlPathTypes(possibleTypes, usedTypes) {
    const unusedTypes: string[] = this.tools.difference(possibleTypes, usedTypes) as string[];
    unusedTypes.forEach((type) => {
      this.urlPathProcessorsMap[type]('');
    });
  }

  private getUrlPathType(urlPath: string): string {
    if (!this.tools.isNil(urlPath) && urlPath !== '') {
      const urlPathArr: string[] = urlPath.split('-');
      const urlPathArrLength = urlPathArr.length;
      return urlPathArr[urlPathArrLength - 1];
    } else {
      return '';
    }
  }

  private getUrlPath(urlPath: string): string {
    if (!this.tools.isNil(urlPath) && urlPath !== '') {
      const urlPathArr: string[] = urlPath.split('-');
      const urlPathArrLength = urlPathArr.length;
      return urlPathArr.slice(0, urlPathArrLength - 1).join('-');
    } else {
      return '';
    }
  }

  private resolveQueryParams(params: Params): void {
    this.pageWasSet = false;

    const paramsKeys: string[] = Object.keys(params).filter((value) => typeof value === 'string') as string[];
    const possibleTypes: string[] = [
      UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE,
      UrlTermsSymbolsEnum.SERVICE_TYPE,
      UrlTermsSymbolsEnum.SYMPTOM,
      UrlTermsSymbolsEnum.PLACE,
    ];
    const usedTypes: string[] = [];

    paramsKeys.forEach((key, index) => {
      if (!this.tools.isNil(this.urlPathProcessorsMap[key])) {
        this.urlPathProcessorsMap[key](params[key]);

        if (possibleTypes.indexOf(key) >= 0) {
          usedTypes.push(key);
        }

        if (key === UrlTermsSymbolsEnum.PAGE) {
          this.pageWasSet = true;
        }
      } else {
        this.state.isRedirectToStateBasedUrlRequired = true;
      }
    });
    if (this.pageWasSet === false) {
      this.updatePage(1);
    }
    if (this.state.page === 1) {
      this.updateDigitalPage(1);
    }

    if (usedTypes.length > 0) {
      this.resetUnusedUrlPathTypes(possibleTypes, usedTypes);
    }

    if (
      this.state.caregiverUnitTypeUrlPath !== '' ||
      this.state.placeUrlPath !== '' ||
      this.state.freeSearchTerm !== '' ||
      this.state.page !== 1 ||
      this.state.locationSearch === true ||
      this.state.openNow === true ||
      this.state.openWeekend === true ||
      this.state.customerFeatureType !== '' ||
      this.state.timeslot != null ||
      this.state.lat !== 0 ||
      this.state.lon !== 0 ||
      Object.keys(this.state.attributes).length > 0
    ) {
      this.state.forceSearchResults = false;
    }
  }

  private updatePage(page: number): void {
    this.state.page = parseInt(page.toString(), 10);
  }

  private updateTimeslot(ts: string[]): void {
    let timeslots: string[] = [];
    if (Array.isArray(ts) === false) {
      timeslots = [(ts as string).toString()];
    } else {
      timeslots = ts;
    }
    const findedTs: SearchTimeslot[] = [];
    timeslots.forEach((tsItem) => {
      findedTs.push(
        (Object.keys(UrlTermsSymbolTimeslotsEnum) as Array<SearchTimeslot>).find(
          (key) => UrlTermsSymbolTimeslotsEnum[key] === tsItem,
        ),
      );
    });
    this.state.timeslot = findedTs;
  }

  private updateAttributes(attributes: string[] | string) {
    if (attributes && Array.isArray(attributes)) {
      attributes.forEach((att) => (this.state.attributes[att] = true));
    } else if (attributes !== '') {
      this.state.attributes[attributes.toString()] = true;
    } else {
      this.state.isRedirectToStateBasedUrlRequired = true;
    }
  }

  private updateDigitalPage(page: number): void {
    this.state.digitalPage = parseInt(page.toString(), 10);
  }

  private updateDebugFlags(flags: string): void {
    this.state.df = flags;
  }

  private updateForceContentPage(f: number) {
    this.state.forceContentPage = parseInt(f.toString(), 10) === 1;
  }

  private updateForceSearchResults(f: number) {
    this.state.forceSearchResults = parseInt(f.toString(), 10) === 1;
  }

  private updateSymptom(urlPath: string) {
    if (urlPath != parseInt(urlPath, 10).toString()) {
      this.state.symptomUrlPath = urlPath;
    }
  }

  private updateTab(tab: string) {
    switch (tab) {
      case RoutesAddonsEnums.SERP_SERVICES:
        this.state.tab = RoutesAddonsEnums.SERP_SERVICES;
        break;
      case RoutesAddonsEnums.SERP_CAREPROVIDERS:
        this.state.tab = RoutesAddonsEnums.SERP_CAREPROVIDERS;
        break;
    }
  }

  private setDefaultSearchTab() {
    if (this.state.tab === null) {
      this.state.tab = this.searchHelper.getDefaultSearchTab(this.state);
    }
  }
}
